import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS_META = {
  backlog:     { label: 'Backlog',     color: '#94A3B8', bg: '#F8FAFC' },
  todo:        { label: 'To Do',       color: '#073E81', bg: '#EFF6FF' },
  in_progress: { label: 'In Progress', color: '#D97706', bg: '#FFFBEB' },
  review:      { label: 'In Review',   color: '#7C3AED', bg: '#F5F3FF' },
  done:        { label: 'Done',        color: '#059669', bg: '#ECFDF5' },
};

const PRIORITY_META = {
  urgent: { label: 'Urgent', color: '#DC2626', bg: '#FEF2F2' },
  high:   { label: 'High',   color: '#EA580C', bg: '#FFF7ED' },
  medium: { label: 'Medium', color: '#D97706', bg: '#FFFBEB' },
  low:    { label: 'Low',    color: '#64748B', bg: '#F8FAFC' },
  none:   { label: '—',      color: '#94A3B8', bg: '#F8FAFC' },
};

function fmtDate(d) {
  if (!d) return null;
  return new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

function initials(name = '') {
  return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2) || '?';
}

function CompletionBar({ rate, total }) {
  if (total === 0) return <span style={{ fontSize: 11, color: 'var(--text3)' }}>No tasks</span>;
  const color = rate >= 80 ? '#059669' : rate >= 50 ? '#D97706' : '#DC2626';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, height: 6, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ width: `${rate}%`, height: '100%', background: color, borderRadius: 4, transition: 'width .4s' }} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 700, color, minWidth: 32 }}>{rate}%</span>
    </div>
  );
}

function InlineDateField({ taskId, field, label, value, isOverdue, isOwner, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const val = value ? value.slice(0, 10) : '';

  const isDue = field === 'due_date';
  const textColor = isDue && isOverdue ? '#DC2626' : '#64748B';
  const icon = isDue ? '📅' : '🚚';

  function commit(newVal) {
    setEditing(false);
    if (newVal !== val) onUpdate(taskId, { [field]: newVal || null });
  }

  if (editing) {
    return (
      <input
        type="date"
        defaultValue={val}
        autoFocus
        onBlur={e => commit(e.target.value)}
        onChange={e => { if (e.target.value) commit(e.target.value); }}
        style={{
          fontSize: 11, padding: '2px 6px', border: '1.5px solid var(--accent)',
          borderRadius: 6, background: 'var(--surface)', color: 'var(--text1)',
          outline: 'none', width: 130,
        }}
      />
    );
  }

  if (!isOwner) {
    if (!val) return null;
    return (
      <span style={{ fontSize: 11, color: textColor, fontWeight: isDue && isOverdue ? 700 : 400 }}>
        {icon} {fmtDate(val)}{isDue && isOverdue ? ' — OVERDUE' : ''}
      </span>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      title={`Edit ${label}`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        background: val ? (isDue && isOverdue ? '#FEF2F2' : 'var(--surface2)') : 'transparent',
        border: val ? `1px solid ${isDue && isOverdue ? '#FECACA' : 'var(--border)'}` : '1px dashed var(--border)',
        borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
        fontSize: 11, color: val ? textColor : 'var(--text3)',
        fontWeight: isDue && isOverdue ? 700 : 400,
        transition: 'border-color .15s, background .15s',
      }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--accent-light)'; }}
      onMouseLeave={e => {
        e.currentTarget.style.borderColor = val ? (isDue && isOverdue ? '#FECACA' : 'var(--border)') : 'var(--border)';
        e.currentTarget.style.background = val ? (isDue && isOverdue ? '#FEF2F2' : 'var(--surface2)') : 'transparent';
      }}
    >
      <span>{icon}</span>
      <span>{val ? `${fmtDate(val)}${isDue && isOverdue ? ' — OVERDUE' : ''}` : label}</span>
      <span style={{ fontSize: 9, opacity: 0.5 }}>✎</span>
    </button>
  );
}

function MemberCard({ m, isOwner, onUpdateTask }) {
  const [open, setOpen] = useState(false);
  const statuses = ['backlog', 'todo', 'in_progress', 'review', 'done'];
  const today = new Date().toISOString().slice(0, 10);

  return (
    <div style={{
      background: 'var(--surface)', border: '1px solid var(--border)',
      borderRadius: 14, overflow: 'hidden',
      boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    }}>
      {/* Card header */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
          <div style={{
            width: 40, height: 40, borderRadius: '50%', background: 'var(--accent)',
            color: '#fff', fontSize: 14, fontWeight: 800,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>{initials(m.name)}</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--text1)' }}>{m.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'capitalize' }}>{m.role}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--accent)', lineHeight: 1 }}>{m.total}</div>
            <div style={{ fontSize: 10, color: 'var(--text3)' }}>tasks</div>
          </div>
        </div>

        {/* Completion bar */}
        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 10, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 5 }}>Completion</div>
          <CompletionBar rate={m.completion_rate ?? 0} total={m.total} />
        </div>

        {/* Status breakdown */}
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {statuses.map(s => {
            const cnt = m.by_status[s] || 0;
            if (!cnt) return null;
            const meta = STATUS_META[s];
            return (
              <span key={s} style={{
                fontSize: 10, fontWeight: 700, padding: '3px 8px', borderRadius: 20,
                background: meta.bg, color: meta.color, border: `1px solid ${meta.color}33`,
              }}>{meta.label} {cnt}</span>
            );
          })}
          {m.total === 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>No tasks assigned</span>}
        </div>
      </div>

      {/* Alerts row */}
      <div style={{ padding: '10px 20px', display: 'flex', gap: 10, background: 'var(--surface2)', flexWrap: 'wrap' }}>
        {m.overdue_count > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#DC2626', display: 'flex', alignItems: 'center', gap: 4 }}>
            ⚠ {m.overdue_count} overdue
          </span>
        )}
        {m.critical_count > 0 && (
          <span style={{ fontSize: 11, fontWeight: 700, color: '#EA580C', display: 'flex', alignItems: 'center', gap: 4 }}>
            🔥 {m.critical_count} urgent/high
          </span>
        )}
        {m.overdue_count === 0 && m.critical_count === 0 && (
          <span style={{ fontSize: 11, color: 'var(--text3)' }}>No urgent issues</span>
        )}
        {m.projects.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
            {m.projects.map(p => (
              <span key={p.id} style={{
                fontSize: 10, padding: '2px 7px', borderRadius: 10,
                background: (p.color || '#073E81') + '22', color: p.color || '#073E81', fontWeight: 600,
              }}>{p.name}</span>
            ))}
          </div>
        )}
      </div>

      {/* Spotlight tasks */}
      {m.spotlight.length > 0 && (
        <div style={{ padding: '0 20px 14px' }}>
          <button
            onClick={() => setOpen(o => !o)}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '10px 0 6px',
              fontSize: 10, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase',
              letterSpacing: '.05em', display: 'flex', alignItems: 'center', gap: 6, width: '100%' }}>
            {open ? '▾' : '▸'} Pending tasks ({m.spotlight.length}{m.total - (m.by_status.done || 0) > m.spotlight.length ? '+' : ''})
          </button>
          {open && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {m.spotlight.map(t => {
                const pm = PRIORITY_META[t.priority] || PRIORITY_META.none;
                const sm = STATUS_META[t.status] || STATUS_META.todo;
                return (
                  <div key={t.id} style={{
                    padding: '10px 12px', borderRadius: 8,
                    background: t.is_overdue ? '#FEF2F2' : 'var(--surface2)',
                    border: `1px solid ${t.is_overdue ? '#FECACA' : 'var(--border)'}`,
                  }}>
                    {/* Title */}
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text1)', marginBottom: 6 }}>{t.title}</div>

                    {/* Badges row */}
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: sm.bg, color: sm.color, fontWeight: 700 }}>{sm.label}</span>
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: pm.bg, color: pm.color, fontWeight: 700 }}>{pm.label}</span>
                      {t.project_name && (
                        <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: (t.project_color || '#073E81') + '22', color: t.project_color || '#073E81', fontWeight: 600 }}>{t.project_name}</span>
                      )}
                    </div>

                    {/* Dates row */}
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      <InlineDateField taskId={t.id} field="due_date" label="Due date" value={t.due_date} isOverdue={t.is_overdue} isOwner={isOwner} onUpdate={onUpdateTask} />
                      <InlineDateField taskId={t.id} field="delivery_date" label="Delivery date" value={t.delivery_date} isOverdue={false} isOwner={isOwner} onUpdate={onUpdateTask} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function TeamTasksPage() {
  const { isOwner } = useAuth();
  const qc = useQueryClient();
  const [projectFilter, setProjectFilter] = useState('');
  const [sortBy, setSortBy] = useState('name'); // name | total | completion | overdue

  const updateTaskMutation = useMutation({
    mutationFn: ({ id, ...data }) => api.patch(`/tasks/${id}`, data),
    onSuccess: () => { qc.invalidateQueries(['team-tasks']); qc.invalidateQueries(['my-tasks']); },
    onError: () => toast.error('Failed to update task'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['team-tasks', projectFilter],
    queryFn: () => api.get(`/team-tasks${projectFilter ? `?project_id=${projectFilter}` : ''}`).then(r => r.data),
    staleTime: 30000,
  });

  const members = data?.members || [];
  const projects = data?.projects || [];

  const sorted = [...members].sort((a, b) => {
    if (sortBy === 'total')      return b.total - a.total;
    if (sortBy === 'completion') return (a.completion_rate ?? 0) - (b.completion_rate ?? 0);
    if (sortBy === 'overdue')    return b.overdue_count - a.overdue_count;
    return a.name.localeCompare(b.name);
  });

  const totalTasks    = members.reduce((s, m) => s + m.total, 0);
  const totalOverdue  = members.reduce((s, m) => s + m.overdue_count, 0);
  const totalCritical = members.reduce((s, m) => s + m.critical_count, 0);
  const avgCompletion = members.length
    ? Math.round(members.reduce((s, m) => s + (m.completion_rate ?? 0), 0) / members.length)
    : 0;

  if (isLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      {/* Page header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text1)', marginBottom: 4 }}>Team Task Overview</div>
        <div style={{ fontSize: 13, color: 'var(--text3)' }}>Task load, progress, and status across the entire team</div>
      </div>

      {/* Summary tiles */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        {[
          { label: 'Total Tasks',       value: totalTasks,    color: 'var(--accent)', sub: 'assigned across team' },
          { label: 'Avg Completion',    value: `${avgCompletion}%`, color: '#059669', sub: 'team average' },
          { label: 'Overdue',           value: totalOverdue,  color: '#DC2626', sub: 'past due date' },
          { label: 'Urgent / High',     value: totalCritical, color: '#EA580C', sub: 'critical priority' },
        ].map(tile => (
          <div key={tile.label} style={{
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 12, padding: '16px 18px',
          }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: tile.color, lineHeight: 1 }}>{tile.value}</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text1)', marginTop: 4 }}>{tile.label}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)' }}>{tile.sub}</div>
          </div>
        ))}
      </div>

      {/* Filters + sort */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', alignItems: 'center' }}>
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
          className="form-input" style={{ fontSize: 12, width: 'auto', minWidth: 180 }}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['name','A–Z'],['total','Most Tasks'],['completion','Lowest %'],['overdue','Most Overdue']].map(([v, label]) => (
            <button key={v} onClick={() => setSortBy(v)}
              style={{
                fontSize: 11, padding: '5px 12px', borderRadius: 20, border: '1px solid var(--border)',
                background: sortBy === v ? 'var(--accent)' : 'var(--surface)',
                color: sortBy === v ? '#fff' : 'var(--text2)',
                fontWeight: sortBy === v ? 700 : 400, cursor: 'pointer',
              }}>{label}</button>
          ))}
        </div>
      </div>

      {/* Member cards */}
      {sorted.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)', fontSize: 14 }}>No team members found.</div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 16 }}>
          {sorted.map(m => <MemberCard key={m.id} m={m} isOwner={isOwner} onUpdateTask={(id, data) => updateTaskMutation.mutate({ id, ...data })} />)}
        </div>
      )}
    </div>
  );
}
